import type maplibregl from 'maplibre-gl'

export const OSM_TILE_URL =
	process.env.REACT_APP_MAP_TILE_URL || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png'

export const OSM_ATTRIBUTION =
	'© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'

/**
 * Satellite imagery, served by MapTiler.
 *
 * Chosen over Esri (whose commercial terms have been drifting toward requiring
 * a key of their own) and over Google (which would reverse the SSP migration
 * away from Google Maps, and whose terms restrict serving their tiles through
 * a non-Google renderer such as MapLibre).
 *
 * The same key can later serve the STREET layer too, which is the way off
 * `tile.openstreetmap.org` — the OSMF tile policy is written for low-volume,
 * non-commercial use. That swap changes the look of every map, so it is a
 * separate change rather than a side effect of this one.
 */
export const MAPTILER_KEY = process.env.REACT_APP_MAPTILER_KEY || ''

/**
 * Whether the satellite basemap can be offered at all.
 *
 * Without a key the tile requests 404 and the map goes blank on toggle, which
 * is worse than having no toggle. Everything downstream — the source, the
 * layer, the control — is gated on this, so an environment with no key behaves
 * exactly as it did before this feature existed.
 */
export const SATELLITE_AVAILABLE = Boolean(MAPTILER_KEY)

export const SATELLITE_TILE_URL = `https://api.maptiler.com/tiles/satellite-v2/{z}/{x}/{y}.jpg?key=${MAPTILER_KEY}`

export const SATELLITE_ATTRIBUTION =
	'© <a href="https://www.maptiler.com/copyright/">MapTiler</a> © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'

export type Basemap = 'street' | 'satellite'

export const STREET_LAYER_ID = 'osm-raster-layer'
export const SATELLITE_LAYER_ID = 'satellite-raster-layer'

const BASEMAP_STORAGE_KEY = 'mdt.map.basemap'

/**
 * The stored basemap preference, or null when the user has never chosen one.
 *
 * The null matters: a screen may want its own default — site capture reads a
 * parcel far better from imagery than from street names — and "the user has
 * expressed no preference" is the only safe moment to apply one. An explicit
 * choice always wins, on every screen.
 *
 * Wrapped because localStorage throws outright in some privacy modes, not just
 * returns null — a map that crashes on mount because of a remembered preference
 * would be a poor trade.
 */
export const readStoredBasemapPreference = (): Basemap | null => {
	try {
		const stored = localStorage.getItem(BASEMAP_STORAGE_KEY)
		if (stored === 'satellite') return SATELLITE_AVAILABLE ? 'satellite' : 'street'
		return stored === 'street' ? 'street' : null
	} catch {
		return null
	}
}

/**
 * The basemap a map should open on.
 *
 * @param fallback what this surface wants when the user has no preference.
 *   Ignored when no satellite key is configured, since there is nothing to
 *   switch to.
 */
export const readStoredBasemap = (fallback: Basemap = 'street'): Basemap => {
	const stored = readStoredBasemapPreference()
	if (stored) return stored
	return fallback === 'satellite' && SATELLITE_AVAILABLE ? 'satellite' : 'street'
}

export const storeBasemap = (basemap: Basemap): void => {
	try {
		localStorage.setItem(BASEMAP_STORAGE_KEY, basemap)
	} catch {
		/* Preference is a convenience; losing it must never break the map. */
	}
}

export const DEFAULT_CENTER: [number, number] = [-98.5795, 39.8283]
export const DEFAULT_ZOOM = 4
// ~1 mile eye altitude — neighborhood scale, comfortable frame for a 1-mile trade-area ring
export const SITE_ZOOM = 15
export const COMPARISON_ZOOM = 10
export const MIN_ZOOM = 2
export const MAX_ZOOM = 18

/**
 * Both basemaps ship in the style; switching toggles layer VISIBILITY.
 *
 * The obvious alternative, `map.setStyle()`, would be a silent bug. Overlays
 * attach themselves imperatively after load — TradeAreaRings, DemographicHeatmap
 * and OverlapZones all call `map.addSource` / `map.addLayer` directly — and
 * setStyle discards every source and layer that is not part of the style being
 * set. Switching basemaps that way would wipe the rings, the heatmap and the
 * overlap zones on every surface, with no error to show for it.
 *
 * Declaring both raster layers up front also fixes their z-order at the bottom
 * of the stack, so overlays added later keep drawing above the imagery.
 */
export const MAP_STYLE: maplibregl.StyleSpecification = {
	version: 8,
	sources: {
		'osm-raster': {
			type: 'raster',
			tiles: [OSM_TILE_URL],
			tileSize: 256,
			attribution: OSM_ATTRIBUTION,
		},
		...(SATELLITE_AVAILABLE
			? {
					'satellite-raster': {
						type: 'raster' as const,
						tiles: [SATELLITE_TILE_URL],
						tileSize: 256,
						attribution: SATELLITE_ATTRIBUTION,
					},
				}
			: {}),
	},
	layers: [
		{
			id: STREET_LAYER_ID,
			type: 'raster',
			source: 'osm-raster',
			minzoom: 0,
			maxzoom: MAX_ZOOM,
		},
		...(SATELLITE_AVAILABLE
			? [
					{
						id: SATELLITE_LAYER_ID,
						type: 'raster' as const,
						source: 'satellite-raster',
						minzoom: 0,
						maxzoom: MAX_ZOOM,
						// Resolved against the stored preference on load.
						layout: { visibility: 'none' as const },
					},
				]
			: []),
	],
}

export const MAP_COLORS = {
	primary: '#0080FE',
	success: '#38A169',
	warning: '#F6AD54',
	destructive: '#E53E3E',
	ringFill: 0.08,
	ringStroke: 0.6,
	ringStrokeWidth: 2,
	overlapFill: '#FFD700',
	overlapFillOpacity: 0.15,
} as const

export const TRADE_AREA_RINGS: number[] = [1, 3, 5]

export const SITE_COLORS: string[] = [
	'#1D9E75',
	'#378ADD',
	'#9B59B6',
	'#E67E22',
	'#E74C3C',
	'#3498DB',
	'#1ABC9C',
	'#F39C12',
]

export function siteColorForIndex(index: number): string {
	return SITE_COLORS[index % SITE_COLORS.length]
}

export function siteLetterForIndex(index: number): string {
	return String.fromCharCode(65 + index)
}
